"use client";

import { useState } from "react";
import {
  CopyButton,
  DataTable,
  Notice,
  type Option,
  SelectField,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import { CARD, HINT, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  convertPinyin,
  markLetter,
  type NumberedU,
  type PinyinOptions,
  type PlainU,
  type Tone,
  type WorkingRow,
} from "@/lib/tool-kit/converters/pinyin";

/** English strings. A translated content file overrides these through `ui`. */
export const PINYIN_TONE_CONVERTER_STRINGS = {
  inputLabel: "Pinyin with tone numbers or tone marks",
  inputHint:
    "Put each tone number after its syllable (ni3 hao3). Type ü as v, u: or ü, and give the neutral tone 5, 0 or no number. Pinyin that already has tone marks works too.",
  sample: "Wo3men5 zai4 Xi1an1 he1 lv4cha2 ba5.",
  neutralLabel: "Neutral tone in tone numbers",
  neutralOptions: {
    five: "5 (ma5)",
    zero: "0 (ma0)",
    none: "No number (ma)",
  },
  numberedULabel: "ü in tone numbers",
  numberedUOptions: {
    v: "v (lv4)",
    colon: "u: (lu:4)",
    umlaut: "ü (lü4)",
  },
  plainULabel: "ü without tone marks",
  plainUOptions: {
    umlaut: "ü (Lü) — catalogs",
    yu: "yu (Lyu) — passports",
    v: "v (Lv)",
    u: "u (Lu)",
  },
  resultsHeading: "Converted",
  marksTitle: "Tone marks",
  marksUse: "Standard pinyin, as dictionaries and textbooks print it.",
  numbersTitle: "Tone numbers",
  numbersUse:
    "For flashcard apps, spreadsheets and keyboards without tone marks.",
  plainTitle: "No tone marks",
  plainUse: "How reference lists and library catalogs write pinyin.",
  copy: "Copy",
  copied: "Copied!",
  enterText: "Type or paste some pinyin to convert.",
  noTones:
    "No tone numbers or tone marks found, so every syllable reads as neutral tone. Put a number after each syllable (ni3 hao3) to get the marks.",
  unread: "Left as typed — not read as pinyin: {words}",
  workingHeading: "Where each tone mark went",
  syllableColumn: "Syllable",
  markedColumn: "With its mark",
  ruleColumn: "Why",
  rules: {
    a: "a takes the mark",
    e: "e takes the mark",
    ou: "o takes the mark in ou",
    only: "the only vowel takes it",
    last: "no a, e or ou: the last vowel takes it",
    nasal: "no vowel: the mark goes on m or n",
  },
  workingCapped: "The first {count} different syllables are shown.",
  vowelsHeading: "Tone-marked vowels",
  vowelColumn: "Vowel",
  toneColumns: {
    first: "1st tone",
    second: "2nd tone",
    third: "3rd tone",
    fourth: "4th tone",
  },
  vowelsNote:
    "Small and capital letters, ready to copy. The neutral tone has no mark.",
} as const;

type Strings = typeof PINYIN_TONE_CONVERTER_STRINGS;
type Neutral = PinyinOptions["neutral"];

/** How many syllables the working table lists before it stops. */
const WORKING_LIMIT = 20;
/** How many unreadable words the notice names. */
const UNREAD_LIMIT = 12;

const VOWEL_ROWS = ["a", "e", "i", "o", "u", "ü"] as const;
const TONES: readonly Tone[] = [1, 2, 3, 4];

function neutralOptions(strings: Strings): Option<Neutral>[] {
  return [
    { value: "5", label: strings.neutralOptions.five },
    { value: "0", label: strings.neutralOptions.zero },
    { value: "", label: strings.neutralOptions.none },
  ];
}

function numberedUOptions(strings: Strings): Option<NumberedU>[] {
  return [
    { value: "v", label: strings.numberedUOptions.v },
    { value: "u:", label: strings.numberedUOptions.colon },
    { value: "ü", label: strings.numberedUOptions.umlaut },
  ];
}

function plainUOptions(strings: Strings): Option<PlainU>[] {
  return [
    { value: "ü", label: strings.plainUOptions.umlaut },
    { value: "yu", label: strings.plainUOptions.yu },
    { value: "v", label: strings.plainUOptions.v },
    { value: "u", label: strings.plainUOptions.u },
  ];
}

function ResultCard({
  title,
  use,
  text,
  strings,
}: {
  title: string;
  use: string;
  text: string;
  strings: Strings;
}) {
  return (
    <div className={`${CARD} space-y-2`}>
      <h3 className={WIDGET_H3}>{title}</h3>
      <p className={HINT}>{use}</p>
      <p className="whitespace-pre-wrap break-words text-foreground text-lg leading-relaxed">
        {text}
      </p>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        small
        text={text}
      />
    </div>
  );
}

function WorkingTable({
  rows,
  strings,
}: {
  rows: readonly WorkingRow[];
  strings: Strings;
}) {
  const fmt = useToolNumber();
  return (
    <section className={`${CARD} space-y-3`}>
      <h2 className={WIDGET_H2}>{strings.workingHeading}</h2>
      <DataTable
        head={[
          strings.syllableColumn,
          strings.markedColumn,
          strings.ruleColumn,
        ]}
        rows={rows
          .slice(0, WORKING_LIMIT)
          .map((row) => [row.numbered, row.marked, strings.rules[row.rule]])}
      />
      {rows.length > WORKING_LIMIT ? (
        <p className={HINT}>
          {t(strings.workingCapped, { count: fmt(WORKING_LIMIT) })}
        </p>
      ) : null}
    </section>
  );
}

function VowelTable({ strings }: { strings: Strings }) {
  const { first, second, third, fourth } = strings.toneColumns;
  return (
    <section className={`${CARD} space-y-3`}>
      <h2 className={WIDGET_H2}>{strings.vowelsHeading}</h2>
      <DataTable
        head={[strings.vowelColumn, first, second, third, fourth]}
        rows={VOWEL_ROWS.map((vowel) => [
          vowel,
          ...TONES.map(
            (tone) =>
              `${markLetter(vowel, tone)} ${markLetter(vowel.toUpperCase(), tone)}`
          ),
        ])}
      />
      <p className={HINT}>{strings.vowelsNote}</p>
    </section>
  );
}

export default function PinyinToneConverterWidget() {
  const strings = useToolUi(PINYIN_TONE_CONVERTER_STRINGS);
  const [text, setText] = useState<string>(strings.sample);
  const [neutral, setNeutral] = useState<Neutral>("5");
  const [numberedU, setNumberedU] = useState<NumberedU>("v");
  const [plainU, setPlainU] = useState<PlainU>("ü");

  const result = convertPinyin(text, { neutral, numberedU, plainU });
  const empty = text.trim() === "";
  const unread = result.unread.slice(0, UNREAD_LIMIT);

  return (
    <div className="space-y-6">
      <TextAreaField
        hint={strings.inputHint}
        id="py-input"
        label={strings.inputLabel}
        onChange={setText}
        prose
        rows={4}
        value={text}
      />
      <div className="grid gap-4 sm:grid-cols-3">
        <SelectField
          id="py-neutral"
          label={strings.neutralLabel}
          onChange={setNeutral}
          options={neutralOptions(strings)}
          value={neutral}
        />
        <SelectField
          id="py-numbered-u"
          label={strings.numberedULabel}
          onChange={setNumberedU}
          options={numberedUOptions(strings)}
          value={numberedU}
        />
        <SelectField
          id="py-plain-u"
          label={strings.plainULabel}
          onChange={setPlainU}
          options={plainUOptions(strings)}
          value={plainU}
        />
      </div>
      {empty ? (
        <Notice>{strings.enterText}</Notice>
      ) : (
        <section aria-live="polite" className="space-y-4">
          <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
          {unread.length > 0 ? (
            <Notice>{t(strings.unread, { words: unread.join(", ") })}</Notice>
          ) : null}
          {result.toned || unread.length > 0 ? null : (
            <Notice>{strings.noTones}</Notice>
          )}
          <ResultCard
            strings={strings}
            text={result.marks}
            title={strings.marksTitle}
            use={strings.marksUse}
          />
          <ResultCard
            strings={strings}
            text={result.numbers}
            title={strings.numbersTitle}
            use={strings.numbersUse}
          />
          <ResultCard
            strings={strings}
            text={result.plain}
            title={strings.plainTitle}
            use={strings.plainUse}
          />
        </section>
      )}
      {!empty && result.working.length > 0 ? (
        <WorkingTable rows={result.working} strings={strings} />
      ) : null}
      <VowelTable strings={strings} />
    </div>
  );
}
